import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  BuildingOfficeIcon,
  CogIcon,
  MapIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { turbineWorkspaceMappingsApi } from '@/services/api/turbineWorkspaceMappings';
import { vblApi } from '@/services/api/vblApi';
import { turbinesApi } from '@/services/api/turbines';
import { workspacesApi } from '@/services/api/workspacesApi';
import { filterOptionsService } from '@/services/api/filterOptions';
import { sitesApi } from '@/services/api/sites';
import { usePermissions, useCurrentUserId } from '@/hooks';
import { AxiosError } from 'axios';
import type { ApiErrorResponse } from '@/types';
import {
  PageToolbar,
  ListCard,
  ConfigCard,
  ModalCard,
  StatsCard,
  type ListItem,
  type ConfigField,
} from '../../components/ui';
import {
  TurbineWorkspaceMapping,
  TurbineWithMapping,
  BulkSiteTurbineWorkspaceMapping,
  validateComponentSelection,
} from '@/types';
import CSVImportModal from './CSVImportModal';

interface MappingModalData {
  mapping_id?: string;
  turbine_id: string;
  turbine_name: string;
  workspace_id: string;
  component_name?: string;
  major_component_name?: string;
}

interface BulkMappingModalData {
  site_id: string;
  site_name: string;
  workspace_id: string;
  component_name?: string;
  major_component_name?: string;
  turbine_filter: {
    turbine_ids?: string[];
    exclude_turbine_ids?: string[];
  };
}

const VBLConfig: React.FC = () => {

  // State management
  const [currentSitePage, setCurrentSitePage] = useState(1);
  const [currentTurbinePage, setCurrentTurbinePage] = useState(1);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');


  // Modal states
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [isBulkMappingModalOpen, setIsBulkMappingModalOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [mappingModalData, setMappingModalData] = useState<MappingModalData>({
    turbine_id: '',
    turbine_name: '',
    workspace_id: '',
    component_name: '',
    major_component_name: '',
  });
  const [bulkMappingModalData, setBulkMappingModalData] = useState<BulkMappingModalData>({
    site_id: '',
    site_name: '',
    workspace_id: '',
    component_name: '',
    major_component_name: '',
    turbine_filter: {},
  });

  const queryClient = useQueryClient();
  const _permissions = usePermissions();

  const userId = useCurrentUserId();

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentTurbinePage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // API Queries
  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ['sites', currentSitePage],
    queryFn: () => sitesApi.getSites({ page: currentSitePage, size: 10 }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: turbinesData, isLoading: turbinesLoading } = useQuery({
    queryKey: ['turbines', selectedSite, currentTurbinePage, debouncedSearchTerm],
    queryFn: () =>
      turbinesApi.getTurbines({
        page: currentTurbinePage,
        size: 20,
        site_id: selectedSite || undefined,
      }),
    enabled: !!selectedSite,
    staleTime: 5 * 60 * 1000,
  });

  const { data: mappingsData, isLoading: mappingsLoading } = useQuery({
    queryKey: ['turbine-mappings', selectedSite],
    queryFn: () =>
      turbineWorkspaceMappingsApi.getMappings({
        site_id: selectedSite || undefined,
        page: 1,
        size: 1000,
      }),
    enabled: !!selectedSite,
    staleTime: 5 * 60 * 1000,
  });

  const { data: workspacesData } = useQuery({
    queryKey: ['vbl-workspaces-for-mapping'],
    queryFn: () => workspacesApi.list({ page: 1, size: 1000, workspaceType: 'vbl' }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['mapping-stats'],
    queryFn: () => turbineWorkspaceMappingsApi.getMappingStats(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: filterMappings } = useQuery({
    queryKey: ['filter-mappings'],
    queryFn: () => filterOptionsService.getFilterMappings(),
    staleTime: 10 * 60 * 1000,
  });

  // Mutations - using vblApi for individual operations
  const createMappingMutation = useMutation({
    mutationFn: (data: {
      turbine_id: string;
      workspace_id: string;
      component_id?: string;
      major_component_id?: string;
    }) => {
      // useCurrentUserId() handles authentication checks internally
      return vblApi.createMapping(data, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
      closeMappingModal();
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to create mapping: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateMappingMutation = useMutation({
    mutationFn: (data: {
      mappingId: string;
      mappingData: {
        turbine_id: string;
        workspace_id: string;
        component_id?: string;
        major_component_id?: string;
      };
    }) => {
      // useCurrentUserId() handles authentication checks internally
      return vblApi.updateMapping(data.mappingId, data.mappingData, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
      closeMappingModal();
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to update mapping: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteMappingMutation = useMutation({
    mutationFn: (mapping: TurbineWorkspaceMapping) => vblApi.deleteMapping(mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to delete mapping: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteBulkMappingsMutation = useMutation({
    mutationFn: (mappings: TurbineWorkspaceMapping[]) => vblApi.deleteBulkMappings(mappings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to delete mappings: ${error.response?.data?.error || error.message}`);
    },
  });

  const bulkCreateMappingMutation = useMutation({
    mutationFn: (data: BulkSiteTurbineWorkspaceMapping) => {
      // useCurrentUserId() handles authentication checks internally
      return turbineWorkspaceMappingsApi.bulkCreateSiteMappings(data, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turbines'] });
      queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
      closeBulkMappingModal();
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to create bulk mappings: ${error.response?.data?.error || error.message}`);
    },
  });

  // Modal Handlers
  const openNewMappingModal = (turbine: TurbineWithMapping) => {
    setMappingModalData({
      turbine_id: turbine.turbine_id,
      turbine_name: turbine.turbine_name,
      workspace_id: '',
      component_name: '',
      major_component_name: '',
    });
    setIsMappingModalOpen(true);
  };

  const openEditMappingModal = (turbine: TurbineWithMapping) => {
    if (!turbine.mapping) return;

    setMappingModalData({
      mapping_id: turbine.mapping.mapping_id,
      turbine_id: turbine.turbine_id,
      turbine_name: turbine.turbine_name,
      workspace_id: turbine.mapping.workspace_id,
      component_name: turbine.mapping.component_name || '',
      major_component_name: turbine.mapping.major_component_name || '',
    });
    setIsMappingModalOpen(true);
  };

  const closeMappingModal = () => {
    setIsMappingModalOpen(false);
    setMappingModalData({
      turbine_id: '',
      turbine_name: '',
      workspace_id: '',
      component_name: '',
      major_component_name: '',
    });
  };

  const submitMappingModal = async () => {
    // Validate component selection
    const validationError = validateComponentSelection(
      mappingModalData.component_name,
      mappingModalData.major_component_name
    );

    if (validationError) {
      alert(validationError.message);
      return;
    }

    // Map component names to IDs using filterMappings
    const component_id = mappingModalData.component_name
      ? filterMappings?.components[mappingModalData.component_name]
      : undefined;
    const major_component_id = mappingModalData.major_component_name
      ? filterMappings?.majorComponents[mappingModalData.major_component_name]
      : undefined;

    if (mappingModalData.mapping_id) {
      const updateData = {
        mappingId: mappingModalData.mapping_id,
        mappingData: {
          turbine_id: mappingModalData.turbine_id,
          workspace_id: mappingModalData.workspace_id,
          component_id,
          major_component_id,
        }
      };
      updateMappingMutation.mutate(updateData);
    } else {
      const newData = {
        turbine_id: mappingModalData.turbine_id,
        workspace_id: mappingModalData.workspace_id,
        component_id,
        major_component_id,
      };
      createMappingMutation.mutate(newData);
    }
  };

  const openBulkMappingModal = (siteId: string, siteName: string) => {
    setBulkMappingModalData({
      site_id: siteId,
      site_name: siteName,
      workspace_id: '',
      component_name: '',
      major_component_name: '',
      turbine_filter: {},
    });
    setIsBulkMappingModalOpen(true);
  };

  const closeBulkMappingModal = () => {
    setIsBulkMappingModalOpen(false);
    setBulkMappingModalData({
      site_id: '',
      site_name: '',
      workspace_id: '',
      component_name: '',
      major_component_name: '',
      turbine_filter: {},
    });
  };

  const submitBulkMappingModal = async () => {
    // Validate component selection
    const validationError = validateComponentSelection(
      bulkMappingModalData.component_name,
      bulkMappingModalData.major_component_name
    );

    if (validationError) {
      alert(validationError.message);
      return;
    }

    const bulkData: BulkSiteTurbineWorkspaceMapping = {
      site_id: bulkMappingModalData.site_id,
      workspace_id: bulkMappingModalData.workspace_id,
      component_name: bulkMappingModalData.component_name || undefined,
      major_component_name: bulkMappingModalData.major_component_name || undefined,
      turbine_filter: bulkMappingModalData.turbine_filter,
    };
    bulkCreateMappingMutation.mutate(bulkData);
  };

  const handleDeleteMapping = React.useCallback((mapping: TurbineWorkspaceMapping) => {
    if (confirm('Are you sure you want to delete this mapping?')) {
      deleteMappingMutation.mutate(mapping);
    }
  }, [deleteMappingMutation]);


  // Handle bulk delete all configs for selected site
  const handleDeleteAllForSite = React.useCallback(() => {
    if (!selectedSite) {
      alert('Please select a site first');
      return;
    }

    // Get all turbine IDs for the selected site
    const siteTurbineIds = turbinesData?.turbines
      .filter(t => t.site_id === selectedSite)
      .map(t => t.turbine_id) || [];

    // Get all mappings for those turbines
    const siteMappings = mappingsData?.mappings.filter(m =>
      siteTurbineIds.includes(m.turbine_id)
    ) || [];

    if (siteMappings.length === 0) {
      alert('No mappings found for the selected site');
      return;
    }

    if (confirm(`Are you sure you want to delete ALL ${siteMappings.length} config(s) for this site?`)) {
      deleteBulkMappingsMutation.mutate(siteMappings);
    }
  }, [selectedSite, turbinesData, mappingsData, deleteBulkMappingsMutation]);


  const handleExportCSV = async () => {
    try {
      const csvContent = await turbineWorkspaceMappingsApi.exportMappingsCSV();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vbl-config-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export CSV');
    }
  };


  // Pagination handlers
  const nextSitePage = () => {
    if (sitesData?.has_more) {
      setCurrentSitePage((prev) => prev + 1);
    }
  };

  const prevSitePage = () => {
    if (currentSitePage > 1) {
      setCurrentSitePage((prev) => prev - 1);
    }
  };

  const nextTurbinePage = () => {
    if (turbinesData?.has_more) {
      setCurrentTurbinePage((prev) => prev + 1);
    }
  };

  const prevTurbinePage = () => {
    if (currentTurbinePage > 1) {
      setCurrentTurbinePage((prev) => prev - 1);
    }
  };

  // Handle site selection
  const handleSiteSelect = (item: ListItem) => {
    setSelectedSite(item.id);
    setCurrentTurbinePage(1);
  };

  // Convert sites data for list display
  const siteListItems: ListItem[] = React.useMemo(() => {
    if (!sitesData?.sites) return [];

    return sitesData.sites.map((site) => {
      // Get stats for this site from statsData if available
      const siteStats = statsData?.mappings_by_site?.[site.site_id];
      const mappedCount = siteStats?.mapped_turbines || 0;
      const totalCount = siteStats?.total_turbines || 0;

      return {
        id: site.site_id,
        primary: site.site_name,
        secondary: totalCount > 0 ? `${mappedCount}/${totalCount} mapped` : 'No turbines',
        tertiary: totalCount > 0 && mappedCount === totalCount ? 'Complete' : 'Incomplete',
        icon: <BuildingOfficeIcon />,
        selected: selectedSite === site.site_id,
        actions: (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openBulkMappingModal(site.site_id, site.site_name);
            }}
            className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
            title="Bulk Edit Site"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        ),
      };
    });
  }, [sitesData, statsData, selectedSite]);

  // Convert turbines data for list display - show each component mapping as separate row
  const turbineListItems: ListItem[] = React.useMemo(() => {
    if (!turbinesData || !mappingsData) return [];

    // Use flatMap to create multiple rows per turbine (one for each component mapping + one for unmapped)
    return turbinesData.turbines.flatMap((turbine) => {
      const turbineMappings = mappingsData?.mappings.filter(m => m.turbine_id === turbine.turbine_id) || [];

      if (turbineMappings.length === 0) {
        // No mappings for this turbine - show single row for creating new mapping
        const turbineWithMapping: TurbineWithMapping = {
          turbine_id: turbine.turbine_id,
          turbine_name: turbine.turbine_name,
          site_id: turbine.site_id,
          site_name: turbine.site_name,
          cod_date: turbine.cod_date,
          model_name: turbine.model_name,
          manufacturer_name: turbine.manufacturer_name,
          mapping: undefined,
        };

        return [{
          id: `${turbine.turbine_id}-unmapped`,
          primary: turbine.turbine_name,
          secondary: 'No workspace assigned',
          tertiary: 'Click + to create mapping',
          icon: <CogIcon />,
          actions: (
            <button
              onClick={() => openNewMappingModal(turbineWithMapping)}
              className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
              title="Create Mapping"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          ),
        }];
      }

      // Has mappings - show each component mapping as separate row
      return turbineMappings.map((mapping) => {
        const turbineWithMapping: TurbineWithMapping = {
          turbine_id: turbine.turbine_id,
          turbine_name: turbine.turbine_name,
          site_id: turbine.site_id,
          site_name: turbine.site_name,
          cod_date: turbine.cod_date,
          model_name: turbine.model_name,
          manufacturer_name: turbine.manufacturer_name,
          mapping,
        };

        // Display component hierarchy: Major Component > Sub-component
        const componentDisplay = mapping.major_component_name
          ? (mapping.component_name
            ? `${mapping.major_component_name} > ${mapping.component_name}`
            : mapping.major_component_name)
          : (mapping.component_name || 'No component');

        return {
          id: `${turbine.turbine_id}-${mapping.mapping_id}`,
          primary: `${turbine.turbine_name}`,
          secondary: `→ ${mapping.workspace_name}`,
          tertiary: `Component: ${componentDisplay}`,
          icon: <CogIcon className="h-5 w-5" />,
          actions: (
            <div className="flex space-x-1">
              <button
                onClick={() => openEditMappingModal(turbineWithMapping)}
                className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
                title="Edit Mapping"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeleteMapping(mapping)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete Mapping"
              >
                <PlusIcon className="h-4 w-4 rotate-45" />
              </button>
              <button
                onClick={() => openNewMappingModal(turbineWithMapping)}
                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                title="Add Another Component Mapping"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          ),
        };
      });
    });
  }, [turbinesData, mappingsData, handleDeleteMapping]);

  // Configuration fields for mapping modal
  const mappingConfigFields: ConfigField[] = [
    {
      id: 'turbine_name',
      label: 'Turbine',
      type: 'text',
      value: mappingModalData.turbine_name,
      disabled: true,
    },
    {
      id: 'workspace_id',
      label: 'VBL Analysis Workspace',
      type: 'select',
      value: mappingModalData.workspace_id,
      options: workspacesData?.workspaces.map(w => ({
        value: w.id,
        label: w.name,
      })) || [],
      required: true,
    },
    {
      id: 'major_component_name',
      label: 'Major Component',
      type: 'select',
      value: mappingModalData.major_component_name || '',
      options: [
        ...(filterMappings ? Object.entries(filterMappings.majorComponents).map(([name, _id]) => ({
          value: name,
          label: name,
        })) : [])
      ],
      required: true,
    },
    {
      id: 'component_name',
      label: 'Sub-component (Optional)',
      type: 'select',
      value: mappingModalData.component_name || '',
      options: [
        ...(filterMappings ? Object.entries(filterMappings.components).map(([name, _id]) => ({
          value: name,
          label: name,
        })) : [])
      ],
      disabled: !mappingModalData.major_component_name,
    },
  ];

  // Configuration fields for bulk mapping modal
  const bulkMappingConfigFields: ConfigField[] = [
    {
      id: 'site_name',
      label: 'Site',
      type: 'text',
      value: bulkMappingModalData.site_name,
      disabled: true,
    },
    {
      id: 'workspace_id',
      label: 'Workspace',
      type: 'select',
      value: bulkMappingModalData.workspace_id,
      options: workspacesData?.workspaces.map(w => ({
        value: w.id,
        label: w.name,
      })) || [],
      required: true,
    },
    {
      id: 'major_component_name',
      label: 'Major Component',
      type: 'select',
      value: bulkMappingModalData.major_component_name || '',
      options: [
        ...(filterMappings ? Object.entries(filterMappings.majorComponents).map(([name, _id]) => ({
          value: name,
          label: name,
        })) : [])
      ],
      required: true,
    },
    {
      id: 'component_name',
      label: 'Sub-component (Optional)',
      type: 'select',
      value: bulkMappingModalData.component_name || '',
      options: [
        ...(filterMappings ? Object.entries(filterMappings.components).map(([name, _id]) => ({
          value: name,
          label: name,
        })) : [])
      ],
      disabled: !bulkMappingModalData.major_component_name,
    },
  ];

  // Handle field changes
  const handleMappingFieldChange = (fieldId: string, value: string | number) => {
    // Allow both major component and sub-component to be selected simultaneously
    setMappingModalData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleBulkMappingFieldChange = (fieldId: string, value: string | number) => {
    // Allow both major component and sub-component to be selected simultaneously
    setBulkMappingModalData((prev) => ({ ...prev, [fieldId]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageToolbar
        title="VBL Config"
        icon={MapIcon}
        buttons={[]}
      />

      <div className="max-w-7xl mx-auto p-6">
        <div className='space-y-6'>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatsCard
              title="Total Turbines"
              value={statsData?.total_turbines || 0}
              description="Across all sites"
              color="bright-blue"
              loading={statsLoading}
            />

            <StatsCard
              title="Mapped Turbines"
              value={statsData?.mapped_turbines || 0}
              description="With workspace assignments"
              color="green"
              loading={statsLoading}
            />

            <StatsCard
              title="Unmapped Turbines"
              value={statsData?.unmapped_turbines || 0}
              description="Require assignment"
              color="purple"
              loading={statsLoading}
            />

            <StatsCard
              title="Workspaces Used"
              value={statsData?.total_workspaces_used || 0}
              description="In active mappings"
              color="dark-blue"
              loading={statsLoading}
            />
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search turbines..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-onyx-bright-blue"
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setIsCSVImportModalOpen(true)}
                className="px-3 py-1.5 bg-onyx-purple text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-dark-blue transition-colors text-sm"
              >
                <ArrowUpTrayIcon className="h-4 w-4 inline mr-1" />
                Import CSV
              </button>
              <button
                onClick={handleExportCSV}
                className="px-3 py-1.5 bg-onyx-purple text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-dark-blue transition-colors text-sm"
              >
                <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Master-Detail Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sites Panel (Left) */}
            <ListCard
              title="Sites"
              subtitle={sitesLoading ? 'Loading...' : `${sitesData?.sites?.length || 0} sites`}
              items={siteListItems}
              onItemClick={handleSiteSelect}
              loading={sitesLoading}
              footerActions={
                sitesData && sitesData.total_pages > 1 ? (
                  <div className="flex items-center justify-between w-full">
                    <p className="text-sm text-gray-700 onyx-body">
                      Page {currentSitePage} of {sitesData.total_pages}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={prevSitePage}
                        disabled={currentSitePage <= 1}
                        className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={nextSitePage}
                        disabled={!sitesData.has_more}
                        className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : undefined
              }
              emptyState={{
                icon: <BuildingOfficeIcon />,
                title: 'No sites found',
                description: 'No sites with turbines are available',
              }}
            />

            {/* Turbines Panel (Right) */}
            <ListCard
              title={selectedSite ? 'Turbines' : 'Select a Site'}
              subtitle={
                !selectedSite
                  ? 'Choose a site from the left panel to view its turbines'
                  : turbinesLoading || mappingsLoading
                    ? 'Loading turbines...'
                    : `${turbinesData?.total_count || 0} turbines at selected site`
              }
              items={turbineListItems}
              loading={selectedSite ? turbinesLoading || mappingsLoading : false}
              headerActions={
                selectedSite && turbineListItems.length > 0 ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleDeleteAllForSite}
                      className="px-3 py-1 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                      title="Delete all configs for this site"
                    >
                      Delete All Site Configs
                    </button>
                  </div>
                ) : undefined
              }
              footerActions={
                selectedSite && turbinesData && turbinesData.total_pages > 1 ? (
                  <div className="flex items-center justify-between w-full">
                    <p className="text-sm text-gray-700 onyx-body">
                      Page {currentTurbinePage} of {turbinesData.total_pages}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={prevTurbinePage}
                        disabled={currentTurbinePage <= 1}
                        className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={nextTurbinePage}
                        disabled={!turbinesData.has_more}
                        className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : undefined
              }
              emptyState={
                !selectedSite
                  ? {
                    icon: <MapIcon />,
                    title: 'Select a Site',
                    description: 'Choose a site from the left panel to view its turbines and mappings',
                  }
                  : {
                    icon: <CogIcon />,
                    title: 'No turbines found',
                    description: 'No turbines found at this site',
                  }
              }
            />
          </div>
        </div>
        {/* Mapping Configuration Modal */}
        <ModalCard
          isOpen={isMappingModalOpen}
          onClose={closeMappingModal}
          title={mappingModalData.mapping_id ? 'Edit VBL Configuration' : 'Create VBL Configuration'}
          subtitle={
            mappingModalData.mapping_id
              ? "Update the workspace and component mapping for this turbine"
              : "Create a new workspace mapping for a specific component. Turbines can have multiple mappings, one per component."
          }
          size="md"
        >
          <ConfigCard
            fields={mappingConfigFields}
            onFieldChange={handleMappingFieldChange}
            onSubmit={submitMappingModal}
            onCancel={closeMappingModal}
            submitLabel={mappingModalData.mapping_id ? 'Update Configuration' : 'Create Configuration'}
            isSubmitting={createMappingMutation.isPending || updateMappingMutation.isPending}
          />
        </ModalCard>

        {/* Bulk Mapping Configuration Modal */}
        <ModalCard
          isOpen={isBulkMappingModalOpen}
          onClose={closeBulkMappingModal}
          title="Bulk Configure Site VBL"
          subtitle="Configure VBL for all turbines at a site"
          size="md"
        >
          <ConfigCard
            fields={bulkMappingConfigFields}
            onFieldChange={handleBulkMappingFieldChange}
            onSubmit={submitBulkMappingModal}
            onCancel={closeBulkMappingModal}
            submitLabel="Create Bulk Configurations"
            isSubmitting={bulkCreateMappingMutation.isPending}
          />
        </ModalCard>

        {/* CSV Import Modal */}
        <CSVImportModal
          isOpen={isCSVImportModalOpen}
          onClose={() => {
            setIsCSVImportModalOpen(false);
          }
          }
        />
      </div>
    </div>
  );
};

export default VBLConfig;